import { useState, useRef, useCallback, useEffect } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { usePermissions } from '@/modules/units/usePermissions'
import type { Role } from '@/lib/permissions/types'
import {
  uploadPartyDocument,
  getPartyDocuments,
  getPartyDocumentUrl,
  deletePartyDocument,
  updatePartyDocumentName,
} from '@/modules/registrations/contacts/contactsService'
import type {
  PartyDocument,
  PartyWithStats,
} from '@/modules/registrations/contacts/contactsTypes'
import { useToast } from '@/shared/hooks/useToast'

interface Props {
  party: PartyWithStats
  userRole?: Role | null
  onClose: () => void
  onUpdated: (party: PartyWithStats) => void
}

export function PartyDocumentModal({ party, userRole, onClose, onUpdated }: Props) {
  const toast = useToast()
  const permissions = usePermissions(userRole)

  const [documents, setDocuments] = useState<PartyDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [documentName, setDocumentName] = useState('')
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load documents on mount
  useEffect(() => {
    const loadDocs = async () => {
      console.log('[PartyDocumentModal] Loading documents for party:', party.id)
      try {
        const docs = await getPartyDocuments(party.id)
        console.log('[PartyDocumentModal] Loaded:', docs.length, 'documents')
        setDocuments(docs)
      } catch (err) {
        console.error('[PartyDocumentModal] Failed to load documents:', err)
      } finally {
        setLoadingDocs(false)
      }
    }
    loadDocs()
  }, [party.id])

  const handleFile = useCallback(
    async (file: File) => {
      if (!permissions.canEditEntries) {
        toast.error('Você não tem permissão para anexar documentos')
        return
      }
      
      setLoading(true)
      try {
        const result = await uploadPartyDocument(
          party.unit_id,
          party.id,
          file,
          documentName || undefined,
        )
        toast.success('Documento enviado com sucesso!')
        setDocuments((prev) => [
          {
            id: result.id,
            party_id: party.id,
            unit_id: party.unit_id,
            document_url: result.document_url,
            document_name: result.document_name,
            created_at: new Date().toISOString(),
            created_by: null,
            updated_at: new Date().toISOString(),
          },
          ...prev,
        ])
        setDocumentName('')
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err)
        toast.error(msg)
      } finally {
        setLoading(false)
      }
    },
    [party, documentName, toast],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleView = async (doc: PartyDocument) => {
    try {
      const url = await getPartyDocumentUrl(doc.document_url)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error('Não foi possível abrir o documento.')
    }
  }

  const handleDelete = async (doc: PartyDocument) => {
    if (!permissions.canEditEntries) {
      toast.error('Você não tem permissão para remover documentos')
      return
    }
    
    if (!window.confirm(`Remover "${doc.document_name}"?`)) return

    try {
      await deletePartyDocument(doc.id, doc.document_url)
      toast.success('Documento removido com sucesso!')
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(msg)
    }
  }

  const handleEditName = async (doc: PartyDocument) => {
    if (!permissions.canEditEntries) {
      toast.error('Você não tem permissão para editar documentos')
      return
    }
    
    if (!editingName.trim()) {
      setEditingId(null)
      return
    }

    try {
      const updated = await updatePartyDocumentName(doc.id, editingName)
      toast.success('Nome atualizado!')
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? updated : d)),
      )
      setEditingId(null)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(msg)
    }
  }

  const headerRight = (
    <div className="flex items-center gap-2">
      <button
        onClick={onClose}
        className="hidden sm:flex p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
        title="Fechar"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <button
        onClick={onClose}
        className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-emerald-600 dark:text-emerald-400"
        title="Confirmar"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )

  return (
    <Modal isOpen onClose={onClose} title="Documentos do Contato" size="md" headerRight={headerRight}>
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
        {/* Documents list */}
        {!loadingDocs && documents.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Documentos anexados ({documents.length})
            </h3>
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700"
                >
                  <svg className="h-5 w-5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>

                  {/* Document name - editable */}
                  {editingId === doc.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleEditName(doc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditName(doc)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      autoFocus
                      className="flex-1 px-2 py-1 text-sm rounded border border-emerald-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                  ) : (
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        setEditingId(doc.id)
                        setEditingName(doc.document_name)
                      }}
                      title="Clique para editar"
                    >
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate hover:underline">
                        {doc.document_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  {editingId !== doc.id && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleView(doc)}
                        className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-colors text-emerald-700 dark:text-emerald-300"
                        title="Abrir"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(doc.id)
                          setEditingName(doc.document_name)
                        }}
                        disabled={!permissions.canEditEntries}
                        className={`p-1.5 rounded transition-colors ${
                          permissions.canEditEntries
                            ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 cursor-pointer'
                            : 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                        }`}
                        title={permissions.canEditEntries ? "Editar nome" : "Sem permissão"}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        disabled={!permissions.canEditEntries}
                        className={`p-1.5 rounded transition-colors ${
                          permissions.canEditEntries
                            ? 'hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-600 dark:text-rose-400 cursor-pointer'
                            : 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                        }`}
                        title={permissions.canEditEntries ? "Deletar" : "Sem permissão"}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loadingDocs && documents.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-2">Nenhum documento anexado ainda</p>
        )}

        {loadingDocs && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Carregando documentos...</p>
        )}

        {/* Document name input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Nome do Novo Documento (opcional)
          </label>
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            placeholder="Ex: Contrato, RG, Comprovante..."
            disabled={loading}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                       px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400
                       focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                       disabled:opacity-50"
          />
        </div>

        {/* Upload area */}
        <div
          onDragOver={() => permissions.canEditEntries && setDragOver(true)}
          onDragLeave={() => setDragOver(false)}
          onDrop={permissions.canEditEntries ? handleDrop : undefined}
          onClick={() => permissions.canEditEntries && fileInputRef.current?.click()}
          className={`relative rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            !permissions.canEditEntries
              ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/30 cursor-not-allowed opacity-60'
              : dragOver
                ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 cursor-pointer'
                : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleInputChange}
            disabled={loading || !permissions.canEditEntries}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx"
            className="hidden"
          />
          <svg className={`h-10 w-10 mx-auto mb-2 ${permissions.canEditEntries ? 'text-slate-400' : 'text-slate-300 dark:text-slate-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className={`text-sm font-medium ${permissions.canEditEntries ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
            {!permissions.canEditEntries
              ? 'Sem permissão para anexar documentos'
              : loading
                ? 'Enviando...'
                : 'Arrastar arquivo aqui ou clicar'}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Máx. 10 MB • PDF, imagem, Word ou Excel
          </p>
        </div>
      </div>
    </Modal>
  )
}
